import React from 'react';
import './App.css';
import axios from "axios";
import Logo from './components/logo';
import AudioRecorder from './components/AudioRecorder';
import ProcessingAnimation from './components/Processinganimation';
import {
  Stethoscope, Activity, FileText, AlertTriangle,
  ClipboardList, AlertCircle, FlaskConical, CalendarClock, Pill, User
} from 'lucide-react';

// ==========================================
// SECTION CONFIG
// ==========================================
type SectionStyle = {
  label: string;
  bg: string;
  border: string;
  labelColor: string;
  textColor: string;
  icon: React.ReactNode;
  highlight?: boolean;
};

const SECTION_STYLES: Record<string, SectionStyle> = {
  'patient demographics': {
    label: 'Patient Demographics',
    bg: '#E6F1FB', border: '#85B7EB', labelColor: '#0C447C', textColor: '#1e3a5f',
    icon: <User size={16} />,
  },
  'visit information': {
    label: 'Visit Information',
    bg: '#E6F1FB', border: '#85B7EB', labelColor: '#0C447C', textColor: '#1e3a5f',
    icon: <Stethoscope size={16} />,
  },
  'chief complaint': {
    label: 'Chief Complaint',
    bg: '#FFF8E1', border: '#F6C90E', labelColor: '#7B5800', textColor: '#4A3200',
    icon: <AlertCircle size={16} />,
    highlight: true,
  },
  'history of present illness': {
    label: 'History of Present Illness',
    bg: '#FAEEDA', border: '#FAC775', labelColor: '#633806', textColor: '#3D2200',
    icon: <Stethoscope size={16} />,
  },
  'past medical history': {
    label: 'Past Medical History',
    bg: '#FBEAF0', border: '#ED93B1', labelColor: '#72243E', textColor: '#4B1528',
    icon: <ClipboardList size={16} />,
  },
  'medications': {
    label: 'Medications',
    bg: '#EAF3DE', border: '#97C459', labelColor: '#27500A', textColor: '#173404',
    icon: <Pill size={16} />,
  },
  'current medications / treatment': {
    label: 'Current Medications / Treatment',
    bg: '#EAF3DE', border: '#97C459', labelColor: '#27500A', textColor: '#173404',
    icon: <Pill size={16} />,
  },
  'social history': {
    label: 'Social History',
    bg: '#F1EFE8', border: '#B4B2A9', labelColor: '#444441', textColor: '#2C2C2A',
    icon: <User size={16} />,
  },
  'reproductive history': {
    label: 'Reproductive History',
    bg: '#FBEAF0', border: '#ED93B1', labelColor: '#72243E', textColor: '#4B1528',
    icon: <ClipboardList size={16} />,
  },
  'diagnostic plan': {
    label: 'Diagnostic Plan',
    bg: '#EEEDFE', border: '#AFA9EC', labelColor: '#3C3489', textColor: '#26215C',
    icon: <FlaskConical size={16} />,
  },
  'assessment': {
    label: 'Assessment',
    bg: '#EEEDFE', border: '#AFA9EC', labelColor: '#3C3489', textColor: '#26215C',
    icon: <FlaskConical size={16} />,
  },
  'treatment plan': {
    label: 'Treatment Plan',
    bg: '#E1F5EE', border: '#5DCAA5', labelColor: '#085041', textColor: '#04342C',
    icon: <CalendarClock size={16} />,
  },
  'follow-up instructions': {
    label: 'Follow-up Instructions',
    bg: '#E1F5EE', border: '#5DCAA5', labelColor: '#085041', textColor: '#04342C',
    icon: <CalendarClock size={16} />,
  },
};

const DEFAULT_STYLE: SectionStyle = {
  label: '', bg: '#F1EFE8', border: '#B4B2A9',
  labelColor: '#444441', textColor: '#2C2C2A',
  icon: <ClipboardList size={16} />,
};

// ==========================================
// MARKDOWN PARSER
// ==========================================
type ParsedSection = { title: string; items: string[] };

function parseMarkdownToSections(md: string): ParsedSection[] {
  if (!md) return [];
  const sections: ParsedSection[] = [];
  let current: ParsedSection | null = null;
  const SKIP_TITLES = ["clinical notes", "emr data", "raw transcript", "prescription"];

  for (const raw of md.split('\n')) {
    const line = raw.trim();
    if (!line || line === '---') continue;

    const h3Match = line.match(/^#{1,6}\s+(.+)$/);
    if (h3Match) {
      if (current && !SKIP_TITLES.includes(current.title.toLowerCase())) sections.push(current);
      const title = h3Match[1].replace(/[🏥📋📝*#]/g, '').trim();
      current = SKIP_TITLES.includes(title.toLowerCase()) ? null : { title, items: [] };
      continue;
    }

    const headingMatch = line.match(/^\*\*(.+?)\*\*:?\s*$/);
    if (headingMatch) {
      if (current && !SKIP_TITLES.includes(current.title.toLowerCase())) sections.push(current);
      const title = headingMatch[1].replace(/:$/, '').trim();
      current = SKIP_TITLES.includes(title.toLowerCase()) ? null : { title, items: [] };
      continue;
    }

    const inlineMatch = line.match(/^\*\*(.+?)\*\*:\s*(.+)$/);
    if (inlineMatch) {
      if (current && !SKIP_TITLES.includes(current.title.toLowerCase())) sections.push(current);
      const title = inlineMatch[1].trim();
      if (!SKIP_TITLES.includes(title.toLowerCase())) {
        current = { title, items: [inlineMatch[2].trim()] };
        sections.push(current);
      }
      current = null;
      continue;
    }

    const bulletMatch = line.match(/^[-•]\s+(.+)$/);
    if (bulletMatch && current) {
      current.items.push(bulletMatch[1].replace(/\*\*(.+?)\*\*/g, '$1').trim());
      continue;
    }

    if (current && !line.startsWith('{') && line.length < 300) {
      current.items.push(line.replace(/\*\*(.+?)\*\*/g, '$1'));
    }
  }

  if (current && !SKIP_TITLES.includes(current.title.toLowerCase())) sections.push(current);
  return sections.filter(s => s.items.length > 0);
}

// ==========================================
// EDITABLE COMPONENTS
// ==========================================
const AutoTextarea: React.FC<{
  value: string;
  onChange: (v: string) => void;
  style?: React.CSSProperties;
  placeholder?: string;
}> = ({ value, onChange, style, placeholder }) => {
  const ref = React.useRef<HTMLTextAreaElement>(null);
  React.useEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = ref.current.scrollHeight + 'px';
    }
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      rows={1}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        background: 'transparent',
        border: '1px dashed transparent',
        borderRadius: 4,
        padding: '2px 6px',
        fontFamily: 'inherit',
        resize: 'none',
        overflow: 'hidden',
        margin: 2,
        outline: 'none',
        ...style,
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = '#94a3b8';
        e.currentTarget.style.background = 'rgba(255,255,255,0.7)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = 'transparent';
        e.currentTarget.style.background = 'transparent';
      }}
    />
  );
};

function EditableSectionCard({
  section,
  onItemChange,
  onAddItem,
  onRemoveItem,
}: {
  section: ParsedSection;
  onItemChange: (itemIdx: number, value: string) => void;
  onAddItem: () => void;
  onRemoveItem: (itemIdx: number) => void;
}) {
  const key = section.title.toLowerCase().replace(/:$/, '');
  const style = SECTION_STYLES[key] ?? { ...DEFAULT_STYLE, label: section.title };

  const HEADER_BG = '#EDF0F3';
  const CARD_BORDER = '#E2E6EA';
  const HEADER_TEXT = '#3F4B5C';
  const BODY_TEXT = '#475569';
  const BULLET = '#94A3B8';

  return (
    <div style={{
      background: '#FFFFFF',
      border: `1px solid ${CARD_BORDER}`,
      borderRadius: 10,
      overflow: 'hidden',
      height: '100%',
    }}>
      <div style={{
        background: HEADER_BG,
        color: HEADER_TEXT,
        fontWeight: 700,
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        padding: '10px 14px',
        borderBottom: `1px solid ${CARD_BORDER}`,
      }}>
        {style.label || section.title}
      </div>

      <div style={{ padding: '10px 14px' }}>
        {section.items.map((item, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            paddingBottom: i < section.items.length - 1 ? 5 : 0,
          }}>
            <span style={{
              marginTop: 9,
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: BULLET,
              flexShrink: 0,
            }} />
            <AutoTextarea
              value={item}
              onChange={(v) => onItemChange(i, v)}
              style={{
                fontSize: 13,
                fontWeight: 400,
                lineHeight: '1.6',
                color: BODY_TEXT,
              }}
            />
            <button
              type="button"
              onClick={() => onRemoveItem(i)}
              title="Remove item"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: HEADER_TEXT, opacity: 0.5, fontSize: 14,
                marginTop: 2, padding: '2px 4px',
              }}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={onAddItem}
          style={{
            marginTop: 8, fontSize: 11, fontWeight: 600,
            color: HEADER_TEXT, background: 'transparent',
            border: `1px dashed ${CARD_BORDER}`, borderRadius: 6,
            padding: '3px 10px', cursor: 'pointer',
          }}
        >
          + Add item
        </button>
      </div>
    </div>
  );
}

function EditableObjectCard({
  data,
  onFieldChange,
}: {
  data: Record<string, any>;
  onFieldChange: (key: string, value: any) => void;
}) {
  return (
    <div style={{ columns: 2, columnGap: 12 }}>
      {Object.entries(data).map(([key, value]) => {
        const label = key.replace(/_/g, ' ');
        const isObject = value !== null && typeof value === 'object';
        return (
          <div key={key} style={{
            breakInside: 'avoid', marginBottom: 12,
            background: '#F8F9FA', border: '1px solid #E2E8F0',
            borderRadius: 10, padding: '10px 14px',
            ...(isObject ? { columnSpan: 'all' as const } : {}),
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: '#64748b',
              textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5,
            }}>
              {label}
            </div>
            {isObject ? (
              <AutoTextarea
                value={JSON.stringify(value, null, 2)}
                onChange={(v) => {
                  try {
                    onFieldChange(key, JSON.parse(v));
                  } catch {
                    onFieldChange(key, v);
                  }
                }}
                style={{ fontSize: 12, color: '#334155', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
              />
            ) : (
              <AutoTextarea
                value={String(value ?? '')}
                onChange={(v) => onFieldChange(key, v)}
                style={{ fontSize: 15, color: '#1e293b', lineHeight: '1.5' }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function renderEditableClinical(
  sections: ParsedSection[],
  onItemChange: (sectionIdx: number, itemIdx: number, value: string) => void,
  onAddItem: (sectionIdx: number) => void,
  onRemoveItem: (sectionIdx: number, itemIdx: number) => void,
): React.ReactNode {
  if (!sections.length)
    return <p className="text-slate-400 italic text-sm">No data.</p>;

  const findIdx = (predicate: (title: string) => boolean) =>
    sections.findIndex(s => predicate(s.title.toLowerCase()));

  const demoIdx = findIdx(t => t.includes('patient demographics'));
  const visitIdx = findIdx(t =>
    t.includes('visit information') || t.includes('visit reason') || t.includes('purpose of visit'),
  );
  const chiefIdx = findIdx(t => t.includes('chief complaint'));

  const topIdxs = new Set([demoIdx, visitIdx, chiefIdx].filter(i => i >= 0));
  const renderEditable = (idx: number) => (
    <EditableSectionCard
      section={sections[idx]}
      onItemChange={(itemIdx, value) => onItemChange(idx, itemIdx, value)}
      onAddItem={() => onAddItem(idx)}
      onRemoveItem={(itemIdx) => onRemoveItem(idx, itemIdx)}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {(demoIdx >= 0 || visitIdx >= 0) && (
        demoIdx >= 0 && visitIdx >= 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {renderEditable(demoIdx)}
            {renderEditable(visitIdx)}
          </div>
        ) : demoIdx >= 0 ? (
          renderEditable(demoIdx)
        ) : (
          renderEditable(visitIdx)
        )
      )}
      {chiefIdx >= 0 && renderEditable(chiefIdx)}
      <div style={{ columns: 2, columnGap: 16 }}>
        {sections.map((_, i) => topIdxs.has(i) ? null : (
          <div key={i} style={{ breakInside: 'avoid', marginBottom: 16 }}>
            {renderEditable(i)}
          </div>
        ))}
      </div>
    </div>
  );
}

function extractTranscript(ekaData: any): string {
  if (!ekaData) return '';

  const t = ekaData.transcript ?? ekaData.transcripts ?? ekaData.transcription;

  if (typeof t === 'string') return t;

  if (t && typeof t === 'object') {
    if (typeof t.transcript === 'string') return t.transcript;
    if (typeof t.text === 'string') return t.text;
    if (typeof t.full_text === 'string') return t.full_text;
    if (typeof t.content === 'string') return t.content;
    if (Array.isArray(t.segments)) {
      return t.segments
        .map((s: any) =>
          typeof s === 'string'
            ? s
            : (s?.text ?? s?.transcript ?? s?.content ?? '')
        )
        .filter(Boolean)
        .join('\n');
    }
    if (Array.isArray(t)) {
      return t
        .map((s: any) =>
          typeof s === 'string'
            ? s
            : (s?.text ?? s?.transcript ?? s?.content ?? '')
        )
        .filter(Boolean)
        .join('\n');
    }
  }

  if (typeof ekaData.raw_transcript === 'string') return ekaData.raw_transcript;
  if (typeof ekaData.text === 'string') return ekaData.text;

  return '';
}

function sectionsToMarkdown(sections: ParsedSection[]): string {
  return sections
    .filter(s => s.items.some(i => i.trim()))
    .map(s => {
      const items = s.items.filter(i => i.trim()).map(i => `- ${i}`).join('\n');
      return `### ${s.title}\n${items}`;
    })
    .join('\n\n');
}

// ==========================================
// CLINICAL → EMR SYNC
// ==========================================
const SECTION_TO_EMR_ALIASES: Record<string, string[]> = {
  'chief complaint': ['chief_complaint', 'chiefComplaint'],
  'history of present illness': ['history_of_present_illness', 'hpi', 'historyOfPresentIllness'],
  'past medical history': ['past_medical_history', 'pmh', 'pastMedicalHistory'],
  'medications': ['medications', 'current_medications'],
  'current medications / treatment': ['current_medications', 'medications', 'treatment'],
  'social history': ['social_history', 'socialHistory'],
  'reproductive history': ['reproductive_history', 'reproductiveHistory'],
  'diagnostic plan': ['diagnostic_plan', 'diagnosticPlan'],
  'assessment': ['assessment'],
  'treatment plan': ['treatment_plan', 'treatmentPlan'],
  'follow-up instructions': ['follow_up_instructions', 'follow_up', 'followUp'],
};

function titleToSnakeCase(title: string): string {
  return title.toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function findEmrKeyForSection(emr: Record<string, any>, sectionTitle: string): string | null {
  const lower = sectionTitle.toLowerCase().trim();
  const candidates = [...(SECTION_TO_EMR_ALIASES[lower] || []), titleToSnakeCase(sectionTitle)];
  for (const c of candidates) {
    if (c in emr) return c;
  }
  return null;
}

function syncEmrFromSection(emr: Record<string, any>, section: ParsedSection): Record<string, any> {
  const key = findEmrKeyForSection(emr, section.title);
  if (!key) return emr;
  // Don't overwrite nested objects with a flat string.
  if (emr[key] !== null && typeof emr[key] === 'object') return emr;
  const value = section.items.filter(i => i.trim()).join('\n');
  if (emr[key] === value) return emr;
  return { ...emr, [key]: value };
}

// ==========================================
// INTERFACES
// ==========================================
interface Props {
  backend_uri:string;
  language: string;
  logo_path: string;
}

interface State {
  prompt_fields: Array<string>;
  session_id: string;
  transcript: string;
  token: string;
  patientJourneyId: string;
  openaiData: any;
  ekaData: any;
  isGeneratingOpenAI: boolean;
  isGeneratingEka: boolean;
  ekaActiveTab: 'clinical' | 'emr' | 'transcript';
  activeProvider: string;
  editableClinicalSections: ParsedSection[];
  editableEmrData: Record<string, any>;
  editableTranscript: string;
  audioUrl: string;
  isSubmitting: boolean;
  isSubmitted: boolean;
}

// ==========================================
// MAIN APP
// ==========================================
class App extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      prompt_fields: [],
      session_id: "",
      transcript: "",
      token: "",
      patientJourneyId: "",
      openaiData: null,
      ekaData: null,
      isGeneratingOpenAI: false,
      isGeneratingEka: false,
      ekaActiveTab: 'clinical',
      editableClinicalSections: [],
      editableEmrData: {},
      editableTranscript: '',
      audioUrl: '',
      isSubmitting: false,
      isSubmitted: false,
      activeProvider: 'anthropic',
    };
  }

  componentDidMount = async () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token') || '';
    const patientJourneyId = params.get('patientJourneyId') || '';
    this.setState({ token, patientJourneyId });

    // Send the Pranaa token on every MediScribe backend call so the backend can
    // verify the caller (enabled there via PRANAA_AUTH_CHECK_URL).
    const backendUri = this.props.backend_uri;
    axios.interceptors.request.use((cfg) => {
      if (token && cfg.url?.startsWith(backendUri)) {
        cfg.headers = cfg.headers ?? {};
        (cfg.headers as any).Authorization = `Bearer ${token}`;
      }
      return cfg;
    });

    this.sessionInit();
  }

  sessionInit = () => {
    axios.post(this.props.backend_uri + "/session_init", { language: this.props.language })
      .then((result: any) => {
        this.setState({
          prompt_fields: result.data.output,
          session_id: result.data.session_id,
        });
      })
      .catch((err: any) => console.log("Error in sessionInit:", err));
  }

  handleTranscript = (newTranscript: string) => {
    this.setState({ transcript: newTranscript });
  }

  handleProcessStart = (provider?: string) => {
    this.setState({
      isGeneratingOpenAI: true,
      isGeneratingEka: true,
      openaiData: null,
      ekaData: null,
      ekaActiveTab: 'clinical',
      editableClinicalSections: [],
      editableEmrData: {},
      editableTranscript: '',
      isSubmitted: false,
      activeProvider: provider || 'ekacare',
    });
  }

  handleOpenAIDataReady = (openaiData: any) => {
    this.setState({ openaiData, isGeneratingOpenAI: false });
  }

  handleAudioUrlReady = (url: string) => {
    this.setState({ audioUrl: url });
  }

  handleEkaDataReady = (ekaData: any) => {
    let sections: ParsedSection[] = [];
    if (ekaData?.clinicalNotes) {
      if (typeof ekaData.clinicalNotes === 'string') {
        sections = parseMarkdownToSections(ekaData.clinicalNotes);
        // OpenAI / Anthropic /summarise returns a plain prose paragraph with no
        // markdown headers, which parseMarkdownToSections yields nothing for.
        // Fall back to a single section so the notes still render.
        if (sections.length === 0) {
          const items = ekaData.clinicalNotes
            .split('\n')
            .map((l: string) => l.replace(/^[-•]\s*/, '').trim())
            .filter(Boolean);
          if (items.length) sections = [{ title: 'Clinical Summary', items }];
        }
      } else {
        sections = Object.entries(ekaData.clinicalNotes)
          .filter(([, v]) => v && String(v).trim())
          .map(([k, v]: [string, any]) => ({
            title: k.replace(/_/g, ' '),
            items: typeof v === 'string'
              ? v.split('\n').map((l: string) => l.replace(/^[-•]\s*/, '').trim()).filter(Boolean)
              : [JSON.stringify(v)],
          }));
      }
    }

    // console.log('[ekaData] raw payload:', ekaData);
    const transcriptStr = extractTranscript(ekaData);
    // console.log('[ekaData] resolved transcript:', transcriptStr);

    this.setState({
      ekaData,
      isGeneratingEka: false,
      editableClinicalSections: sections,
      editableEmrData: ekaData?.emrData ? { ...ekaData.emrData } : {},
      editableTranscript: transcriptStr,
      isSubmitted: false,
    });
  }

  handleClinicalItemChange = (sectionIdx: number, itemIdx: number, value: string) => {
    this.setState(prev => {
      const newSections = prev.editableClinicalSections.map((s, i) =>
        i === sectionIdx
          ? { ...s, items: s.items.map((it, j) => j === itemIdx ? value : it) }
          : s
      );
      return {
        editableClinicalSections: newSections,
        editableEmrData: syncEmrFromSection(prev.editableEmrData, newSections[sectionIdx]),
      };
    });
  }

  handleClinicalAddItem = (sectionIdx: number) => {
    this.setState(prev => {
      const newSections = prev.editableClinicalSections.map((s, i) =>
        i === sectionIdx ? { ...s, items: [...s.items, ''] } : s
      );
      return {
        editableClinicalSections: newSections,
        editableEmrData: syncEmrFromSection(prev.editableEmrData, newSections[sectionIdx]),
      };
    });
  }

  handleClinicalRemoveItem = (sectionIdx: number, itemIdx: number) => {
    this.setState(prev => {
      const newSections = prev.editableClinicalSections.map((s, i) =>
        i === sectionIdx ? { ...s, items: s.items.filter((_, j) => j !== itemIdx) } : s
      );
      return {
        editableClinicalSections: newSections,
        editableEmrData: syncEmrFromSection(prev.editableEmrData, newSections[sectionIdx]),
      };
    });
  }

  handleEmrFieldChange = (key: string, value: any) => {
    this.setState(prev => ({
      editableEmrData: { ...prev.editableEmrData, [key]: value },
    }));
  }

  handleTranscriptEdit = (value: string) => {
    this.setState({ editableTranscript: value });
  }

  handleSubmitToPranaa = async () => {
    const {
      editableClinicalSections, editableEmrData, editableTranscript,
      audioUrl, ekaData, token, patientJourneyId, activeProvider,
    } = this.state;

    if (!ekaData) return;

    this.setState({ isSubmitting: true });
    try {
      // removedStatements is review-only UI data; Pranaa's API doesn't expect it.
      const { removedStatements, ...submittedEkaData } = ekaData;
      const payload = {
        ekascribe: {
          ...submittedEkaData,
          clinicalNotes: sectionsToMarkdown(editableClinicalSections),
          emrData: editableEmrData,
          transcript: editableTranscript,
        },
        audio_url: audioUrl,
        provider: activeProvider,
      };

      console.log('Submitting to Pranaa with payload:', payload);
      await axios.post(
        `https://api.pranaa.in/patient-journey/${patientJourneyId}/questionnaire/conversation`,
        // `https://test.api.pranaa.in/patient-journey/${patientJourneyId}/questionnaire/conversation`,
        { result: payload },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } },
      );
      console.log('Submitted to Pranaa successfully');
      this.setState({ isSubmitted: true });
    } catch (err) {
      console.error('Error submitting to Pranaa:', err);
    } finally {
      this.setState({ isSubmitting: false });
    }
  }

  render() {
    const {
      isGeneratingOpenAI, isGeneratingEka, openaiData, ekaData, ekaActiveTab,
      editableClinicalSections, editableEmrData, editableTranscript,
      isSubmitting, isSubmitted, activeProvider
    } = this.state;

    const providerLabels: { [key: string]: string } = {
      ekacare: 'Eka.care',
      openai: 'OpenAI',
      anthropic: 'Anthropic (Claude)',
    };
    const providerLabel = providerLabels[activeProvider] || activeProvider;


    const ekaTabs = [
      { key: 'clinical' as const, label: 'Clinical Notes', icon: <Stethoscope size={14} /> },
      { key: 'emr' as const, label: 'EMR Data', icon: <Activity size={14} /> },
      { key: 'transcript' as const, label: 'Transcript', icon: <FileText size={14} /> },
    ];



    return (
      <React.Fragment>
        {/* ── Header ── */}
        <header className="header">
          <div className="header-brand">
            <div className="header-logo">
              <Logo logo_path={this.props.logo_path} />
            </div>
            <div className="header-title">
              <h1>MediScribe</h1>
              <span className="header-subtitle">AI Clinical Documentation</span>
            </div>
          </div>
          <div className="header-meta">
            <span className="header-pill">
              <span className="dot" />
              System Online
            </span>
          </div>
        </header>

        <main className="app-shell">

          {/* ── Page header ── */}
          <div>
            <div className="section-eyebrow">
              <Stethoscope size={13} />
              Consultation Workspace
            </div>
            <div className="section-title">Record &amp; Document Patient Visit</div>
            <div className="section-helper">
              Capture audio, generate structured clinical notes, and sync to the patient record in one flow.
            </div>
          </div>

          {/* ── Workspace split: Step 2 (left) / Step 1 (right) ── */}
          <div className="workspace-split">
            {/* ── Pipeline output (left) ── */}
            <div className="workspace-output">
              {(isGeneratingOpenAI || openaiData || isGeneratingEka || ekaData) ? (
                <div className="pipeline-card eka-card">
                <div className="pipeline-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ color: 'var(--ink-900)' }}>{providerLabel} Clinical Assessment</span>
                  </div>
                  {isGeneratingEka ? (
                    <span className="pipeline-status is-loading">
                      <span className="dot" />
                      Processing
                    </span>
                  ) : ekaData ? (
                    <span className="pipeline-status is-success">
                      <span className="dot" />
                      Ready
                    </span>
                  ) : null}
                </div>

                <div className="pipeline-body">
                  {ekaData ? (
                    <>
                      {/* Tab bar */}
                      <div style={{
                        display: 'flex', gap: 4, borderBottom: '1px solid var(--border-soft)',
                        marginBottom: 18,
                      }}>
                        {ekaTabs.map(tab => {
                          const active = ekaActiveTab === tab.key;
                          return (
                            <button
                              key={tab.key}
                              onClick={() => this.setState({ ekaActiveTab: tab.key })}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '10px 14px', fontSize: 13, fontWeight: 600,
                                color: active ? 'var(--ink-700)' : 'var(--slate-500)',
                                background: 'transparent', border: 'none',
                                borderBottom: `2px solid ${active ? 'var(--sky-500)' : 'transparent'}`,
                                marginBottom: -1, cursor: 'pointer', borderRadius: 0,
                              }}
                            >
                              {tab.icon}
                              {tab.label}
                            </button>
                          );
                        })}
                      </div>

                      {/* Triage card — clinical tab only */}
                      {ekaActiveTab === 'clinical' && ekaData.triage?.symptoms?.length > 0 && (
                        <div style={{
                          background: ekaData.triage.level === 'HIGH RISK' ? 'rgba(245, 34, 45, 0.05)' : 'var(--sky-50)',
                          border: `1px solid ${ekaData.triage.level === 'HIGH RISK' ? 'rgba(245, 34, 45, 0.25)' : 'var(--sky-300)'}`,
                          borderRadius: 12, padding: 14, marginBottom: 16,
                        }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            paddingBottom: 8, marginBottom: 10,
                            borderBottom: `1px solid ${ekaData.triage.level === 'HIGH RISK' ? 'rgba(245, 34, 45, 0.18)' : 'var(--sky-300)'}`,
                          }}>
                            <AlertTriangle size={16} color={ekaData.triage.level === 'HIGH RISK' ? '#F5222D' : '#4a5585'} />
                            <h4 style={{
                              fontSize: 13, fontWeight: 700,
                              color: ekaData.triage.level === 'HIGH RISK' ? '#9F1B23' : '#26356B',
                            }}>
                              MedAI Triage Assessment: {ekaData.triage.level}
                            </h4>
                          </div>
                          {ekaData.triage.flags?.length > 0 && (
                            <ul style={{
                              listStyle: 'disc', paddingLeft: 18, margin: '0 0 12px',
                              color: '#9F1B23', fontSize: 12, fontWeight: 500,
                            }}>
                              {ekaData.triage.flags.map((flag: string, idx: number) => (
                                <li key={idx} style={{ marginBottom: 2 }}>{flag}</li>
                              ))}
                            </ul>
                          )}
                          <p style={{
                            fontSize: 11, color: 'var(--slate-500)', marginBottom: 8,
                            textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700,
                          }}>
                            Extracted Symptoms
                          </p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {ekaData.triage.symptoms.map((sym: string, idx: number) => (
                              <span key={idx} style={{
                                padding: '3px 10px', background: 'white',
                                border: '1px solid var(--border-soft)', borderRadius: 6,
                                fontSize: 12, fontWeight: 600, color: 'var(--ink-700)',
                              }}>
                                {sym}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tab content */}
                      <div style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}>
                        {ekaActiveTab === 'clinical' && ekaData.removedStatements?.length > 0 && (
                          <details style={{
                            background: 'var(--bg-muted)', border: '1px solid var(--border-soft)',
                            borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13,
                          }}>
                            <summary style={{ cursor: 'pointer', color: 'var(--ink-700)', fontWeight: 600 }}>
                              {ekaData.removedStatements.length} drafted statement{ekaData.removedStatements.length === 1 ? ' was' : 's were'} left
                              out because the transcript doesn't support {ekaData.removedStatements.length === 1 ? 'it' : 'them'}
                            </summary>
                            <p style={{ color: 'var(--slate-500)', margin: '8px 0 6px' }}>
                              Review these; add any that are actually correct to the note by hand.
                            </p>
                            <ul style={{ listStyle: 'disc', paddingLeft: 18, margin: 0, color: 'var(--ink-700)' }}>
                              {ekaData.removedStatements.map((r: any, idx: number) => (
                                <li key={idx} style={{ marginBottom: 4 }}>
                                  <strong>{r.section}:</strong> {r.text}
                                  <span style={{ color: 'var(--slate-500)' }}> ({r.reason})</span>
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                        {ekaActiveTab === 'clinical' && (
                          editableClinicalSections.length > 0
                            ? renderEditableClinical(
                                editableClinicalSections,
                                this.handleClinicalItemChange,
                                this.handleClinicalAddItem,
                                this.handleClinicalRemoveItem,
                              )
                            : <p style={{ color: 'var(--slate-400)', fontStyle: 'italic', fontSize: 13 }}>No clinical notes generated.</p>
                        )}
                        {ekaActiveTab === 'emr' && (
                          Object.keys(editableEmrData).length > 0
                            ? <EditableObjectCard data={editableEmrData} onFieldChange={this.handleEmrFieldChange} />
                            : <p style={{ color: 'var(--slate-400)', fontStyle: 'italic', fontSize: 13 }}>No EMR data generated.</p>
                        )}
                        {ekaActiveTab === 'transcript' && (
                          <div style={{
                            background: 'var(--bg-muted)', borderRadius: 10,
                            border: '1px solid var(--border-soft)', padding: 16,
                          }}>
                            <AutoTextarea
                              value={editableTranscript}
                              onChange={this.handleTranscriptEdit}
                              placeholder="No transcript available."
                              style={{
                                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                                fontSize: 13, color: 'var(--ink-900)', lineHeight: '1.7',
                                border: '1px solid var(--border)', background: 'white',
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Submit footer */}
                      <div style={{
                        marginTop: 22, paddingTop: 18,
                        borderTop: '1px solid var(--border-soft)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                      }}>
                        <span style={{ fontSize: 12, color: 'var(--slate-500)' }}>
                          Review the generated output above, then sync to Pranaa.
                        </span>
                        <button
                          type="button"
                          onClick={this.handleSubmitToPranaa}
                          disabled={!ekaData || isSubmitting || isSubmitted}
                          className={isSubmitted ? 'submitted-btn' : 'submit-btn'}
                          style={{
                            padding: '10px 20px', fontSize: 13, fontWeight: 600,
                            cursor: (!ekaData || isSubmitting || isSubmitted) ? 'not-allowed' : 'pointer',
                            opacity: isSubmitting ? 0.7 : 1,
                          }}
                        >
                          {isSubmitting ? 'Submitting…' : isSubmitted ? 'Submitted ✓' : 'Submit to Pranaa'}
                        </button>
                      </div>
                    </>
                  ) : (
                    isGeneratingEka
                      ? <ProcessingAnimation />
                      : <p style={{ color: 'var(--danger)', fontSize: 13 }}>Processing failed or no data returned.</p>
                  )}
                </div>
              </div>
              ) : (
                <div className="workspace-output-placeholder">
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-900)', marginBottom: 8 }}>
                    AI Clinical Assessment
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--slate-500)', lineHeight: 1.6 }}>
                    Record or upload an audio clip on the right, then generate insights to see structured clinical notes here.
                  </p>
                </div>
              )}
            </div>

            {/* ── Recorder card (right) ── */}
            <section className="surface-card workspace-recorder">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 18,
                paddingBottom: 14,
                borderBottom: '1px solid var(--border-soft)',
              }}>
                <div>
                  <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink-900)' }}>
                    Recording Audio
                  </h2>
                </div>
              </div>

              <AudioRecorder
                backend_uri={this.props.backend_uri}
                session_id={this.state.session_id}
                language={this.props.language}
                onTranscriptChange={this.handleTranscript}
                onProcessStart={this.handleProcessStart}
                onOpenAIDataReady={this.handleOpenAIDataReady}
                onEkaDataReady={this.handleEkaDataReady}
                onAudioUrlReady={this.handleAudioUrlReady}
              />
            </section>
          </div>
        </main>

        <footer className="footer">
          © {new Date().getFullYear()} MediScribe AI · Built for clinicians
        </footer>
      </React.Fragment>
    );
  }
}

export default App;